<script setup lang="ts">
import { computed } from 'vue'
import MainNavigation from '@voix/components/chrome/MainNavigation.vue'
import TemplatizerNavigation from '@voix/components/templatizer/TemplatizerNavigation.vue'

import VoixMessages from '@voix/components/chrome/messages/VoixMessages.vue'
import VoixReleasesNotification from '@voix/components/chrome/revisions/VoixReleasesNotfication.vue'
import VoixSiteAuth from '@voix/voix-layer/components/VoixSiteAuth.vue'

import SpinnerLoader from '@voix/components/chrome/Loader.vue'

import { useAdminStore } from '@voix/store/adminStore'

import { useVoixUserAuth } from '@voix/composables/useVoixUserAuth'
import { getCurrentUser } from '@voix/composables/queries/useQueryCurrentUser'

import { useRoute } from '#imports'

const adminStore = useAdminStore()
const route = useRoute()

const { pending } = await getCurrentUser()

const section = computed(() => {
  if (route.path.includes('studio'))
    return 'editor'

  if (route.path.includes('settings'))
    return 'settings'

  if (route.path.includes('templatizer'))
    return 'templatizer'

  return 'unknown'
})

const { checkUserPermissions } = useVoixUserAuth()
const canViewAdmin = await checkUserPermissions(['Administration.*'])

const isLoginTypeRoute = computed(() => {
  const exceptions = ['voix-login-en', 'voix-auth-callback-en']

  if (exceptions.includes(route.name as string))
    return true

  return false
})
</script>

<template>
  <VoixAdminTheme>
    <VoixSiteAuth :exceptions="['voix-login-en', 'voix-auth-callback-en']">
      <div v-if="!pending">
        <div v-if="canViewAdmin">
          <div
            id="voix-popper"
            class="z-[5000000000] font-voix-studio text-sm bg-gray-800 text-gray-300 border border-gray-600 rounded shadow p-2 opacity-0 pointer-events-none transition-all duration-200 ease-out"
          />
          <div class="fixed z-[100000] inset-0 pointer-events-none font-voix-studio">
            <div id="voix-admin-root" class="pointer-events-auto" />
          </div>
          <div
            class="fixed z-[99999] inset-0 voix-admin-bg-lightest font-voix-studio antialiased"
          >
            <div
              class="absolute z-30 pointer-events-none top-0 left-0 right-0 flex duration-200 ease-out"
              :class="{ '-translate-x-16': adminStore.isZenMode }"
            >
              <MainNavigation />
            </div>

            <TemplatizerNavigation
              class="transform duration-200 ease-out fixed z-20 top-0 left-0 bottom-0 shadow-2xl shadow-gray-500"
              :class="{
                '-translate-x-64': section !== 'templatizer',
                'translate-x-[80px]': section === 'templatizer',
              }"
            />

            <div
              class="ml-[80px] h-screen overflow-y-auto"
            >
              <slot />

              <VoixMessages />
              <VoixReleasesNotification class="relative z-20 isolate" />
            </div>
            <div
              v-show="adminStore.isLoading"
              class="fixed z-50 inset-0 flex justify-center items-center pointer-events-none"
            >
              <SpinnerLoader class="w-8 h-8" />
            </div>

            <div class="fixed inset-0 pl-[80px] flex justify-stretch items-stretch pointer-events-none ">
              <div class="relative  w-full h-full">
                <div id="voix-portal-admin-content" class="pointer-events-auto" />
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="isLoginTypeRoute">
          <slot />
        </div>
        <div v-else class="flex flex-col voix-admin-bg-lightest space-y-20 justify-center items-center h-screen">
          <div class="w-full flex flex-col items-center justify-center max-w-[900px]">
            <div class="w-full text-xl font-bold uppercase voix-admin-text border-b voix-admin-border-light pb-1">
              You are not allowed to access this page
            </div>
            <div class="mt-3 w-full flex justify-end space-x-4">
              <NuxtLink
                to="/voix/login"
                class="p-3 px-6 rounded-sm font-medium voix-admin-text-lightest voix-admin-bg-dark text-sm"
              >
                Login
              </NuxtLink>
              <NuxtLink
                to="/"
                class="p-3 px-6 rounded-sm font-medium voix-admin-text-lightest voix-admin-bg-dark text-sm"
              >
                Go to home
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="fixed z-50 inset-0 flex justify-center items-center pointer-events-none"
      >
        <SpinnerLoader class="w-8 h-8" />
      </div>
    </VoixSiteAuth>
  </VoixAdminTheme>
</template>
